import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Toolbar from '@mui/material/Toolbar';
import { useLocalizedStrings } from '../../localization/LocalizedStringsProvider';
import { useAlert } from './alert/AlertProvider';
import Spacer from './Spacer';

export interface SaveFooterProps {
    formId?: string;
    formIsDirty?: boolean;
    saving?: boolean;
    saveButtonTitle?: string;
    cancelButtonTitle: string;
    // Shown instead of cancelButtonTitle when there is nothing to cancel. These screens save in place and
    // stay open, so after saving "Cancel" reads as though it would undo the save — the only way out of the
    // screen looked like a way to lose the work.
    //
    // Defaults to the localized "Close", so a screen gets this by being a SaveFooter rather than by
    // remembering to ask. It used to be opt-in, and four of the five screens using this footer never opted
    // in — including the location editor, which was reported as still saying Cancel after a save while the
    // brand editor beside it said Close (TTD-145). Pass it only to override the wording.
    closeButtonTitle?: string;
    // Leaving the screen. Runs when there is nothing pending — the button reads "Close" then.
    onCancel: (event: React.MouseEvent<HTMLButtonElement>) => void;
    // Throwing away pending edits and STAYING. Runs when there are unsaved changes and the user confirms.
    //
    // Cancel used to discard and then leave, which fired the discard prompt and then the unsaved-changes
    // prompt on the way out — two dialogs for one decision (TTD-161). Cancel now cancels the EDITS and Close
    // closes the SCREEN, which is what Skinning and Revenue Centers already did; brands, locations and
    // onboarding were the odd ones out.
    //
    // Optional: a screen that cannot revert in place falls back to onCancel, keeping the old behaviour.
    onDiscard?: () => void;
    // Drops the Save button for a user with read access only, leaving Close so they can still get out.
    //
    // Screens used to handle this by hiding the whole footer, which took the only way off the page with it
    // (TTD-159). Note this hides the CONTROL, not the form: the fields above stay editable, because on the
    // tabbed screens the obvious way to disable them — one fieldset around the form — also disables the tab
    // bar, which is the bug Dialog.test.tsx exists to catch.
    readOnly?: boolean;
    startContent?: React.ReactNode;
}

// Matches the permanent navigation drawer width in Frame, so the fixed footer sits beside the drawer
// (aligned with the content area) instead of underneath it — otherwise left-aligned content is hidden.
const drawerWidth = 240;

const SaveFooter = ({ formId, formIsDirty, saving, saveButtonTitle, cancelButtonTitle, closeButtonTitle, onCancel, onDiscard, readOnly, startContent }: SaveFooterProps) => {
    const alert = useAlert();
    const strings = useLocalizedStrings();

    const dismissTitle = formIsDirty ? cancelButtonTitle : (closeButtonTitle ?? strings.close);

    const handleCancel = async (event: React.MouseEvent<HTMLButtonElement>) => {     
        if (!formIsDirty) {
            onCancel(event);
            return;
        }

        const agree = await alert({
            title: "Discard Changes",
            message: "Are you sure you want to discard your changes?",
            destructive: true,
            agreeButtonTitle: "Discard",
            closeButtonTitle: "Cancel",
        });

        if (!agree) return;

        // Revert in place when the screen knows how; otherwise leave, as before.
        if (onDiscard) onDiscard();
        else onCancel(event);
    }

    return (
        <>        
            <Toolbar /> 
            <AppBar position="fixed" sx={{ top: 'auto', bottom: 0, bgcolor: "background.paper", left: `${drawerWidth}px`, width: `calc(100% - ${drawerWidth}px)` }}>
                <Toolbar>
                    {startContent}
                    <Box sx={{ flexGrow: 1 }} />
                    <Button variant="outlined" onClick={handleCancel}>{dismissTitle}</Button>
                    {!readOnly && <>
                    <Spacer x={2} />
                    <Button variant="contained" type="submit" form={formId} disabled={!formIsDirty} loading={saving}>{saveButtonTitle}</Button>
                    </>}
                </Toolbar>
            </AppBar>
        </>
    );
}

export default SaveFooter;